<script lang="ts" setup>
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import { useBreadcrumb } from '@menu-lib/breadcrumb/breadcrumb';
import { useTussenpersonenGet } from '@services/crm-internal/orval-api/tussenpersonen-api/tussenpersonen-api';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { VButton } from '@shared/component/VButton';
import VMenuButton from '@shared/component/VMenuButton/VMenuButton.vue';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import type { BasicColor } from '@tailwind-preset/colors';
import { getSurenetUrl } from '@menu-lib/urlHelper';
import VChip from '@shared/component/VChip/VChip.vue';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VTooltip } from '@shared/component/VTooltip';
import { type CrmAdviseursGetAdviseursDto } from '@services/crm-internal/orval-api/model/crmAdviseursGetAdviseursDto';
import { SUREbusinessCommonDomainMarktsegment } from '@services/crm-internal/orval-api/model/sUREbusinessCommonDomainMarktsegment';
import { useRouter } from 'vue-router';
import { useClassicExportHtmlToExcel } from './aspClassicExport';

const { data, error, isPending } = useTussenpersonenGet(
  { inclusiefMaatschappijen: true },
  {
    query: {
      select: (res) =>
        res.result.map((r) => ({
          ...r,
          status:
            r.status === 'Geblokkeerd'
              ? 'Inactief'
              : r.status === 'Beeindigd'
                ? 'Beëindigd'
                : r.status,
          accountmanager: r.status !== 'Actief' ? '-' : r.accountmanager,
          // Aparte velden per marktsegment zodat beide kolommen los sorteerbaar en
          // filterbaar zijn (bijv. alleen-particulier: Particulier=Ja + Zakelijk=Nee).
          // Ja/Nee i.p.v. booleans zodat de CSV-export leesbaar is.
          particulier: r.marktsegmenten.includes(SUREbusinessCommonDomainMarktsegment.Particulier)
            ? 'Ja'
            : 'Nee',
          zakelijk: r.marktsegmenten.includes(SUREbusinessCommonDomainMarktsegment.Zakelijk)
            ? 'Ja'
            : 'Nee',
        })),
    },
  },
);
useWatchErrorHandler(error);

const statusTagColors: Record<string, BasicColor> = {
  Actief: 'green',
  Beëindigd: 'red',
  Inactief: 'orange',
  Kandidaat: 'grey',
  Onbekend: 'grey',
};

const typeColors: Record<string, BasicColor> = {
  Demo: 'blue',
  Maatschappij: 'teal',
};

const router = useRouter();

function onRowClicked(row: any) {
  router.push({ name: 'Adviseurgegevens', params: { adviseurnummer: row.data.id } });
}

function getWarningTooltipText(dto: CrmAdviseursGetAdviseursDto) {
  if (dto.aanvragenGeblokkeerd && dto.beheerstatus) {
    return 'Beperkte rechten (beheerstatus) en aanvragen geblokkeerd';
  }
  if (dto.aanvragenGeblokkeerd) {
    return 'Aanvragen geblokkeerd';
  }
  return 'Beperkte rechten (beheerstatus)';
}

const { mutate, isPending: exportPending, error: exportError } = useClassicExportHtmlToExcel();
useWatchErrorHandler(exportError);

useBreadcrumb([{ name: 'Adviseurbeheer', link: '' }], undefined);
</script>
<template>
  <VPageContainer title="Adviseurbeheer">
    <template #actions>
      <VButtonGroup attached>
        <VButton
          href="/app/crm/samenwerkingsovereenkomst/overzicht"
          inverted
          prepend-icon="far fa-file-signature"
        >
          SWO Overzicht
        </VButton>
        <VMenuButton
          :button-props="{ inverted: true, loading: exportPending }"
          menu-position="under"
          :menu-props="{
            model: [
              {
                label: 'Adviseurs',
                command: () =>
                  mutate({
                    urlScript:
                      '/content/beheer/intern/tussenpersonen/exporteeralletpgegevensnaarexcel.asp',
                    bestandsnaam: 'Adviseurs.xlsx',
                  }),
                icon: 'far fa-building',
              },
              {
                label: 'Gebruikers',
                command: () =>
                  mutate({
                    urlScript:
                      '/content/beheer/intern/tussenpersonen/exporteerallegebruikersgegevensnaarexcel.asp',
                    bestandsnaam: 'Gebruikers.xlsx',
                  }),

                icon: 'far fa-user',
              },
              {
                label: 'DVO',
                url: '/app/beheerbox/rapportage/rapport/dvoexport',
                icon: 'far fa-money-check-dollar-pen',
              },
            ],
          }"
        >
          Exporteren
        </VMenuButton>
        <VMenuButton
          :button-props="{ inverted: true }"
          menu-position="under"
          :menu-props="{
            model: [
              {
                label: 'Adviseur',
                url: getSurenetUrl(
                  '/content/beheer/intern/tussenpersonen/details/algemeen/algemeen/?tussenpersoonnr=&maatschappijinlog=',
                  false,
                ),
                icon: 'far fa-building',
              },
              {
                label: 'Maatschappij',
                url: getSurenetUrl(
                  '/content/beheer/intern/tussenpersonen/details/algemeen/algemeen/?tussenpersoonnr=&maatschappijinlog=1',
                  false,
                ),
                icon: 'far fa-landmark',
              },
            ],
          }"
        >
          Nieuw
        </VMenuButton>
      </VButtonGroup>
    </template>
    <VSkeleton v-if="isPending" :count="20" height="lg" />
    <VDataTable
      @row-click="onRowClicked"
      show-row-arrow
      :filterable-properties="
        [
          { field: 'status', header: 'Status' },
          { field: 'type', header: 'Type' },
          { field: 'particulier', header: 'Particulier' },
          { field: 'zakelijk', header: 'Zakelijk' },
          { field: 'accountmanager', header: 'Accountmanager' },
          { field: 'beheerstatus', header: 'Beheerstatus' },
          { field: 'aanvragenGeblokkeerd', header: 'Aanvragen geblokkeerd' },
        ] as any
      "
      v-else
      searchable
      exportable
      :value="data"
      :value-colors="{ status: statusTagColors }"
      state-storage="session"
      state-key="adviseurbeheerTable"
      current-page-report-template="van {totalPages} ({totalRecords})"
    >
      <VColumn sortable field="id" header="Nummer" />
      <VColumn sortable field="name" header="Naam">
        <template #body="{ data: row }">
          <div class="flex items-center gap-2">
            <VChip
              :color="typeColors[row.type]"
              class="h-6"
              :label="row.type"
              v-if="row.type !== 'Regulier'"
            />
            <VTooltip
              v-if="row.beheerstatus || row.aanvragenGeblokkeerd"
              :label="getWarningTooltipText(row)"
            >
              <FontAwesomeIcon class="text-orange" icon="far fa-circle-exclamation" />
            </VTooltip>
            {{ row.name }}
          </div>
        </template>
      </VColumn>
      <VColumn sortable field="particulier" header="Particulier" align="center">
        <template #body="{ data: row }">
          <FontAwesomeIcon v-if="row.particulier === 'Ja'" icon="fa-light fa-check" />
        </template>
      </VColumn>
      <VColumn sortable field="zakelijk" header="Zakelijk" align="center">
        <template #body="{ data: row }">
          <FontAwesomeIcon v-if="row.zakelijk === 'Ja'" icon="fa-light fa-check" />
        </template>
      </VColumn>
      <VColumn sortable field="" header="Adres">
        <template #body="{ data: row }">
          {{ row.bezoekadres?.straat }} {{ row.bezoekadres?.huisnummer
          }}{{ row.bezoekadres?.huisnummertoevoeging }}
        </template>
      </VColumn>
      <VColumn sortable field="bezoekadres.plaats" header="Plaats" />
      <VColumn sortable field="accountmanager" header="Accountmanager" />
      <VColumn field="status" header="Status">
        <template #body="{ data: row }">
          <VStatusTag inverted :color="statusTagColors[row.status]">
            {{ row.status }}
          </VStatusTag>
        </template>
      </VColumn>
    </VDataTable>
  </VPageContainer>
</template>
